import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarCheck,
  CalendarDays,
  Check,
  CircleCheck,
  BellRing,
  Globe,
  Megaphone,
  PiggyBank,
  Repeat,
  Sparkles,
  Star,
  Users,
  X,
} from 'lucide-react';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { FerdyMopping } from '@/components/marketing/FerdyMopping';
import { HeroVideo } from '@/components/marketing/HeroVideo';
import { CtaLink, Eyebrow, Reveal, SectionHeading } from '@/components/marketing/primitives';
import { ProductTour } from '@/components/marketing/ProductTour';
import { ProductShot } from '@/components/marketing/ProductShot';
import scheduleRequestsShot from '@/assets/product/schedule-requests.webp';
import requestDialogShot from '@/assets/product/booking-request-dialog.webp';
import scheduleTeamShot from '@/assets/product/schedule-team.webp';
import { cn } from '@/lib/utils';
import { planByName } from '@/lib/marketingSeo';

/**
 * SaaS sales landing page rendered at the apex domain root.
 * Deep indigo hero, Ferdy the mascot mopping, kinetic statement, pain → gain,
 * three pillars, proof band, product preview and a pricing teaser.
 */
const MarketingHome = () => (
  <MarketingLayout>
    <Hero />
    <KineticStatement />
    <OldWayNewWay />
    <Workflow />
    <Pillars />
    <ProofBand />
    <ProductPreview />
    <ProductTour />
    <PricingTeaser />
  </MarketingLayout>
);

/* ---------------------------------- Hero ---------------------------------- */

// What owners get, scrolling under the hero (all live features).
const platformBenefits = [
  'Built for Google and AI search',
  'Online booking 24/7',
  'Card payments and deposits up front',
  '0% commission on your jobs',
  'Instant or approved bookings',
  'Repeat cleans on autopilot',
  'Quotes customers accept online',
  'AI logo maker',
  'AI-written quotes',
  'Cleaner logins and a shared schedule',
  'No double bookings',
  'Automatic email reminders',
  'Live Google reviews on your site',
  'Checklists and photo reports',
  'Connect your own domain',
  'Insights on revenue and leads',
  'Export your data any time',
];

// One line on every screen: on phones the text and gaps shrink with the width.
const HeroTrust = () => (
  <ul
    data-testid="hero-trust"
    className="flex flex-nowrap items-center justify-center gap-x-[clamp(0.4rem,2.6vw,1.5rem)] whitespace-nowrap text-[length:clamp(10px,3.15vw,14px)] text-white/65 sm:text-sm"
  >
    {['Free for 30 days', 'No setup fee', 'Cancel any time'].map((t) => (
      <li key={t} className="flex items-center gap-[clamp(0.3rem,1.4vw,0.5rem)]">
        <CircleCheck className="h-[1.1em] w-[1.1em] shrink-0 text-[hsl(var(--mk-mint))]" /> {t}
      </li>
    ))}
  </ul>
);

const Hero = () => {
  const leftRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const ledeRef = useRef<HTMLParagraphElement>(null);
  // Ferdy is pulled left by the empty space after the paragraph's longest line,
  // so he always stands just beside the words however they wrap.
  const [ferdyPull, setFerdyPull] = useState(0);
  const rightRef = useRef<HTMLDivElement>(null);
  // Desktop: the widescreen video's top lines up with the headline and its
  // bottom with the buttons (narrower if the screen runs out of width).
  const [videoBox, setVideoBox] = useState<{ top: number; width: number } | null>(null);

  useEffect(() => {
    // Layout offsets (offsetTop) ignore the entrance animations' transforms, so
    // the measurement is right even if it runs mid-animation or in a background tab.
    const offsetWithin = (el: HTMLElement, ancestor: HTMLElement) => {
      let top = 0;
      let node: HTMLElement | null = el;
      while (node && node !== ancestor) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      return top;
    };
    const measure = () => {
      const lede = ledeRef.current;
      if (lede) {
        const range = document.createRange();
        range.selectNodeContents(lede);
        const textRight = Math.max(...Array.from(range.getClientRects(), (r) => r.right));
        const slack = Number.isFinite(textRight) ? lede.getBoundingClientRect().right - textRight : 0;
        setFerdyPull(Math.max(0, Math.round(slack) - 6));
      }
      const left = leftRef.current;
      const head = headlineRef.current;
      const cta = ctaRef.current;
      if (!left || !head || !cta || !window.matchMedia('(min-width: 1024px)').matches) {
        setVideoBox(null);
        return;
      }
      const top = offsetWithin(head, left);
      // The video stops level with the top of the buttons.
      const bottom = offsetWithin(cta, left);
      const right = rightRef.current;
      const room = right ? window.innerWidth - right.getBoundingClientRect().left - 24 : Infinity;
      const width = Math.round(Math.max(0, Math.min(((bottom - top) * 16) / 9, room)));
      setVideoBox((prev) => (prev && prev.top === top && prev.width === width ? prev : { top, width }));
    };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    const timers = [window.setTimeout(measure, 600), window.setTimeout(measure, 1500)];
    const observer = new ResizeObserver(measure);
    if (leftRef.current) observer.observe(leftRef.current);
    window.addEventListener('resize', measure);
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
  <section className="mk-ink-bg mk-on-dark relative overflow-hidden">
    <div className="container relative mx-auto grid items-center gap-12 px-4 pb-12 pt-[108px] lg:grid-cols-[1fr_1fr] lg:items-start lg:gap-8 lg:pb-0">
      <div ref={leftRef} className="relative z-10 max-w-2xl lg:pb-16">
        <Reveal>
          {/* Same pill as the other pages' hero labels; one line even on small phones. */}
          <Eyebrow className="whitespace-nowrap text-[length:clamp(11px,3.3vw,0.8rem)]">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--mk-sun))]" /> Websites &amp; software for cleaning businesses
          </Eyebrow>
        </Reveal>
        <Reveal delay={80} className="relative mt-7">
          {/* Sized per breakpoint so each phrase stays on one line in the hero column. */}
          <h1 ref={headlineRef} className="mk-display text-[2.05rem] leading-[1.06] text-white sm:text-[3.4rem] md:text-[4rem] lg:text-[length:min(2.85rem,6.6vh)] xl:text-[length:min(3.6rem,6.6vh)] 2xl:text-[length:min(4.2rem,6.6vh)]">
            <span className="block whitespace-nowrap">Build your website.</span>
            <span className="block whitespace-nowrap">Manage your bookings.</span>
            <span className="mk-mark mk-mark-block mt-1 whitespace-nowrap text-[hsl(var(--mk-ink))]">Grow your business.</span>
          </h1>
        </Reveal>
        <Reveal delay={160} className="mt-8 flex items-center gap-2 lg:mt-[min(2rem,3.2vh)] lg:items-end">
          <p ref={ledeRef} className="mk-lede min-w-0 flex-1 text-white/70 lg:text-[length:clamp(1rem,min(1.6vw,2.7vh),1.35rem)] [text-wrap:pretty] sm:max-w-[30rem]">
            Everything you need to run and grow your cleaning business, all in one place. Build a professional,
            SEO and AI optimised website to attract customers, take online bookings, manage jobs and schedules,
            and keep your business running smoothly.{' '}
            <span className="text-white">All with zero booking{'\u00a0'}commissions.</span>
          </p>
          {/* Ferdy mops opposite the copy: halfway up it on phones, his puddle level with the last line on desktop. */}
          <div
            className="relative w-[72px] shrink-0 sm:w-[104px] lg:mb-2 xl:w-[118px]"
            style={ferdyPull ? { transform: `translateX(-${ferdyPull}px)` } : undefined}
          >
            <FerdyMopping />
          </div>
        </Reveal>
        <Reveal delay={240} className="mt-10 lg:mt-[min(2.5rem,4vh)]">
          <div ref={ctaRef} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <CtaLink to="/signup">Start your free trial</CtaLink>
          <CtaLink to="/demo" variant="ghost">
            Look round a demo site
          </CtaLink>
          </div>
        </Reveal>
      </div>

      {/* Product screens drifting up the right-hand side */}
      <div ref={rightRef} className="relative lg:-mr-[12vw]">
        <svg
          viewBox="0 0 500 600"
          className="ferdy-squiggle pointer-events-none absolute -left-16 -top-24 hidden h-[70%] w-[70%] lg:block"
          aria-hidden
        >
          <path
            d="M430 20c40 90-20 170-110 150S210 60 280 40s120 80 60 160-200 60-220 170 70 170 200 190"
            fill="none"
            style={{ stroke: 'hsl(var(--mk-mint))' }}
            strokeWidth="30"
            strokeLinecap="round"
            opacity="0.85"
          />
        </svg>
        <HeroVideo
          className="relative z-10 mx-auto max-w-[640px] lg:mx-0"
          style={videoBox ? { marginTop: videoBox.top, width: videoBox.width, maxWidth: 'none' } : undefined}
        />
        {/* The reassurance ticks sit under the video, centred on it, on every screen size. */}
        <Reveal
          delay={320}
          className="relative z-10 mx-auto mt-6 max-w-[640px] lg:mx-0"
          style={videoBox ? { width: videoBox.width, maxWidth: 'none' } : undefined}
        >
          <HeroTrust />
        </Reveal>
      </div>
    </div>

    {/* Platform benefits marquee */}
    <div className="relative py-6">
      <div className="mk-marquee overflow-hidden">
        <div className="mk-marquee-track gap-10 pr-10">
          {[...platformBenefits, ...platformBenefits].map((s, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap font-display text-lg text-white/55">
              {s}
              <Sparkles className="h-4 w-4 text-[hsl(var(--mk-mint))]" />
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

/* --------------------------- Kinetic statement ---------------------------- */

const kineticLines: {
  before: string;
  icon: typeof Globe;
  tint: string;
  after: string;
  dir: 1 | -1;
  highlight?: boolean;
}[] = [
  { before: 'Your website', icon: Globe, tint: 'bg-[hsl(var(--mk-lavender))]', after: 'gets you found,', dir: -1 },
  { before: 'online booking', icon: CalendarDays, tint: 'bg-[hsl(var(--mk-sun))]', after: 'fills the diary,', dir: 1 },
  { before: 'and your team', icon: Users, tint: 'bg-[hsl(var(--mk-mint))]', after: 'just turns up.', dir: -1 },
];

const KineticStatement = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      el.style.setProperty('--mk-shift', String((progress - 0.5) * 220));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="overflow-hidden bg-[hsl(var(--mk-panel))] py-28 md:py-36">
      <div className="container mx-auto px-4 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mk-muted))]">
          Less admin. More bookings.
        </p>
      </div>
      <div ref={ref} className="mt-10 space-y-3 md:space-y-5">
        {kineticLines.map(({ before, icon: Icon, tint, after, dir, highlight }) => (
          <div
            key={before}
            className="mk-kinetic-line flex items-center justify-center gap-3 whitespace-nowrap font-display text-[clamp(1.9rem,5.4vw,4.6rem)] font-semibold leading-none tracking-[-0.028em] md:gap-6"
            style={{ ['--mk-dir' as string]: dir }}
          >
            <span className={cn(highlight && 'mk-mark')}>{before}</span>
            <span
              className={cn(
                'inline-flex h-[0.95em] w-[1.25em] shrink-0 items-center justify-center rounded-[0.28em] text-[hsl(var(--mk-ink))]',
                tint
              )}
              aria-hidden
            >
              <Icon className="h-[0.5em] w-[0.5em]" strokeWidth={2.2} />
            </span>
            <span className={cn(highlight && 'mk-mark')}>{after}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------------------------- Old way / new way --------------------------- */

const oldWay = [
  'Bookings lost somewhere in a WhatsApp thread',
  'Quotes scribbled on the back of a receipt',
  'A Facebook page pretending to be a website',
  'Nowhere to be seen when someone googles a cleaner',
  'A group text every morning to say who’s going where',
  'Chasing the same £45 for the third week running',
];

const newWay = [
  'A website that shows up when people search nearby, on Google or in ChatGPT',
  'Fixed-price cleans: customers pick a free slot and book, day or night',
  'Quote-only jobs: they send a request, you price it, they accept online',
  'Regulars booked in on repeat, week after week',
  'One diary, and each cleaner sees only their own jobs',
  'Card customers pay when they book, so no-shows don’t cost you',
  'Change a price or a photo yourself, in seconds',
];

const OldWayNewWay = () => (
  <section className="py-28">
    <div className="container mx-auto px-4">
      <SectionHeading
        eyebrow="Sound familiar?"
        className="max-w-4xl"
        title={
          <>
            You didn’t start a cleaning business to do admin at 9pm.
          </>
        }
        lede="Last clean done, feet up at last, and the phone goes again: “Any chance of Thursday?” Let your website answer that one. Customers see your free slots and book themselves in, and you get your evening back."
      />

      <div className="mt-16 grid gap-5 lg:grid-cols-2">
        <Reveal className="mk-panel p-8 md:p-10">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--mk-muted))]">
            The old way
          </p>
          <ul className="mt-6 space-y-4">
            {oldWay.map((item) => (
              <li key={item} className="flex items-start gap-3 text-lg text-[hsl(var(--mk-muted))]">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-ink)/0.08)]">
                  <X className="h-3.5 w-3.5" />
                </span>
                <span className="line-through decoration-[hsl(var(--mk-ink)/0.25)]">{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={120} className="mk-ink-bg mk-on-dark relative overflow-hidden rounded-[2.5rem] p-8 md:p-10">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--mk-mint))]">
            The Polished way
          </p>
          <ul className="mt-6 space-y-4">
            {newWay.map((item) => (
              <li key={item} className="flex items-start gap-3 text-lg text-white">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-mint))] text-[hsl(var(--mk-ink))]">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-9">
            <CtaLink to="/signup" size="sm">
              Get your evenings back
            </CtaLink>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* -------------------------------- Workflow -------------------------------- */

const workflowSteps = [
  { label: 'Booked', hint: 'They pick a clean and a slot' },
  { label: 'Paid', hint: 'By card, in full or a deposit' },
  { label: 'Confirmed', hint: 'Instantly, or once you approve' },
  { label: 'Assigned', hint: 'You pick, or we put one on' },
];

const Workflow = () => (
  <section className="pb-28">
    <div className="container mx-auto px-4">
      <SectionHeading
        eyebrow="You set the rules, they book"
        title={
          <>
            From a Google search to <span className="mk-mark mk-mark-mint">a paid job.</span>
          </>
        }
        lede="You’re in complete control of your services, pricing and availability. Customers can book and pay online, with the option to pay in full or leave a deposit. Choose whether bookings are confirmed automatically or require your approval. Once confirmed, assign the job yourself or let the system automatically allocate an available cleaner. From booking to job completion, everything is organised and managed in one place."
      />
      <Reveal delay={120} className="mx-auto mt-14 max-w-5xl">
        <div className="rounded-[2rem] bg-[hsl(var(--mk-lilac))] p-3 md:p-5" data-testid="marketing-booking-workflow">
          <div className="relative">
            <ProductShot
              src={scheduleRequestsShot}
              alt="The schedule with a new booking request waiting in the Unassigned column"
              width={1600}
              height={1000}
            />
            <img
              src={requestDialogShot}
              alt="A booking request opened: date, address, customer, and a field to add a cleaner"
              width={720}
              height={954}
              loading="lazy"
              decoding="async"
              className="mk-float absolute right-[3%] top-[14%] hidden w-[34%] rounded-2xl shadow-[0_40px_70px_-24px_rgba(20,16,70,0.6)] ring-1 ring-black/5 sm:block"
            />
          </div>
          <ol className="mt-3 grid grid-cols-2 gap-2 md:mt-4 md:grid-cols-4">
            {workflowSteps.map((step, i) => (
              <li key={step.label} className="flex items-center gap-3 rounded-2xl bg-white px-3.5 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--mk-ink))] font-display text-sm font-semibold text-white">
                  {i + 1}
                </span>
                <span className="min-w-0 leading-tight">
                  <span className="block font-display text-sm font-semibold">{step.label}</span>
                  <span className="block truncate text-xs text-[hsl(var(--mk-muted))]">{step.hint}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </Reveal>
    </div>
  </section>
);

/* --------------------------------- Pillars -------------------------------- */

const pillars = [
  {
    icon: Megaphone,
    tint: 'bg-[hsl(var(--mk-sun))]',
    title: 'Get found',
    body: 'Someone types “end of tenancy clean Cardiff” into Google, or asks ChatGPT who’s good near them. Your site is set up to be found for exactly that, and when they land, your Google star rating and reviews are right there to win them over.',
  },
  {
    icon: CalendarCheck,
    tint: 'bg-[hsl(var(--mk-mint))]',
    title: 'Get booked',
    body: 'You decide how each service is sold. Give it a price and customers book a free slot in under a minute, even at 11pm on a Sunday. Make it quote only and they send a request, you price it, and they accept online.',
  },
  {
    icon: Users,
    tint: 'bg-[hsl(var(--mk-lavender))]',
    title: 'Get it done',
    body: 'Every booking lands on one schedule. Put a cleaner on it and it appears in their own login. Invoices, payments and how the month is going are all in the same place.',
  },
];

const extras = [
  { icon: Repeat, label: 'Repeat cleans' },
  { icon: BellRing, label: 'Email reminders' },
  { icon: Star, label: 'Google reviews' },
  { icon: PiggyBank, label: 'Deposits' },
];

// Mirrored word for word in the crawler copy (src/lib/marketingSeo.ts).
const AI_HOME_LINE =
  'The AI logo maker designs you a logo in seconds (Team and Business), and the AI proposal writer turns a few notes about a job into a priced quote (Business).';

const Pillars = () => (
  <section className="px-4">
    <div className="mk-panel container mx-auto px-6 py-20 md:px-14 md:py-24">
      <SectionHeading
        eyebrow="How it fits together"
        title="Found, booked and sorted."
        lede="Most cleaning businesses run on a website, a booking app and a group chat, and hope nothing slips through the gaps. Here it’s all one thing. The booking made on your site is the same booking on your schedule, on your cleaner’s phone and on the invoice."
      />

      <div className="mt-16 grid gap-px overflow-hidden rounded-[2rem] bg-[hsl(var(--border))] md:grid-cols-3">
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 100} className="group flex flex-col bg-white p-8 md:p-10">
            <span className={cn('mk-icon-blob text-[hsl(var(--mk-ink))]', p.tint)}>
              <p.icon className="h-6 w-6" strokeWidth={2} />
            </span>
            <h3 className="mt-8 text-2xl">{p.title}</h3>
            <p className="mt-3 flex-1 text-[hsl(var(--mk-muted))]">{p.body}</p>
            <Link
              to="/features"
              className="mt-8 inline-flex h-11 w-11 items-center justify-center self-end rounded-full border border-[hsl(var(--mk-ink)/0.2)] transition-all group-hover:border-[hsl(var(--mk-ink))] group-hover:bg-[hsl(var(--mk-ink))] group-hover:text-white"
              aria-label={`Learn more: ${p.title}`}
            >
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-center">
        <span className="text-[hsl(var(--mk-muted))]">Also included:</span>
        {extras.map((e) => (
          <span key={e.label} className="flex items-center gap-2 font-display text-lg font-semibold">
            <e.icon className="h-5 w-5 text-[hsl(var(--mk-violet))]" /> {e.label}
          </span>
        ))}
      </Reveal>

      {/* The AI tools, called out on their own: they're not on every plan. */}
      <Reveal className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-3 rounded-[1.75rem] border border-[hsl(var(--mk-ink)/0.12)] bg-white px-6 py-5 text-center sm:flex-row sm:text-left">
        <span className="mk-icon-blob shrink-0 bg-[hsl(var(--mk-sun))] text-[hsl(var(--mk-ink))]">
          <Sparkles className="h-5 w-5" strokeWidth={2} />
        </span>
        <p className="text-[hsl(var(--mk-muted))]">
          <strong className="font-semibold text-[hsl(var(--mk-ink))]">New: AI tools.</strong> {AI_HOME_LINE}{' '}
          <Link to="/features#ai-tools" className="font-semibold text-[hsl(var(--mk-violet))] underline underline-offset-4">
            See how
          </Link>
        </p>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------- Proof band ------------------------------ */

const ProofBand = () => (
  <section className="py-28 md:py-36">
    <div className="container mx-auto grid gap-16 px-4 lg:grid-cols-[1.2fr_1fr] lg:items-center">
      <Reveal>
        <p className="font-display text-[clamp(4.5rem,13vw,10rem)] font-semibold leading-[0.9] tracking-[-0.06em]">
          <span className="mk-pill-stat">60</span> sec
        </p>
        <p className="mk-lede mt-6 max-w-md text-[hsl(var(--mk-muted))]">
          From signing up to a booking website with your name on it. Add your prices tonight and you
          could be taking bookings by breakfast.
        </p>
      </Reveal>

      <div className="grid gap-5">
        {[
          {
            big: '£0',
            title: 'taken from your jobs',
            body: 'You pay the plan and that’s it. Stripe charges its usual card fee, but we never take a slice.',
            tint: 'bg-[hsl(var(--mk-sun))]',
          },
          {
            big: '30',
            title: 'days to try it properly',
            body: 'Build your site, take real bookings and bring the team in. If it isn’t earning its keep, walk away.',
            tint: 'bg-[hsl(var(--mk-lavender))]',
          },
          {
            big: '0',
            title: 'contracts to sign',
            body: 'It’s month to month. Cancel any time and take your customers and bookings with you.',
            tint: 'bg-[hsl(var(--mk-mint))]',
          },
        ].map((s, i) => (
          <Reveal key={s.title} delay={i * 100} className="mk-card flex items-center gap-6 p-6">
            <span
              className={cn(
                'flex h-20 w-24 shrink-0 items-center justify-center rounded-2xl font-display text-4xl font-semibold tracking-tight',
                s.tint
              )}
            >
              {s.big}
            </span>
            <span>
              <span className="block font-display text-xl font-semibold">{s.title}</span>
              <span className="mt-1 block text-[hsl(var(--mk-muted))]">{s.body}</span>
            </span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------------------- Product preview ---------------------------- */

const previewBullets = [
  'New bookings and quote requests waiting in one place',
  'Your services, prices and how long each clean takes',
  'Regulars on repeat: weekly, fortnightly or monthly',
  'A login for each cleaner, showing only their jobs',
  'Invoices your customers can pay by card or PayPal',
  'How the month’s going, at a glance',
  'Your website, edited right there, no code',
];

const ProductPreview = () => (
  <section className="px-4">
    <div className="mk-ink-bg mk-on-dark container mx-auto overflow-hidden rounded-[2.5rem] px-6 py-20 md:px-14 md:py-24">
      <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <Reveal>
            <Eyebrow>The back office</Eyebrow>
            <h2 className="mk-h2 mt-6 text-white">The back office, minus the office.</h2>
            <p className="mk-lede mt-5 text-white/65">
              Open it with your morning cuppa and you’ll know who’s where, what’s booked in and who
              still owes you. No spreadsheets, no twelve open tabs, no scrolling back through texts.
            </p>
          </Reveal>
          <ul className="mt-9 space-y-3.5">
            {previewBullets.map((b, i) => (
              <Reveal as="li" key={b} delay={i * 70} className="flex items-center gap-3 text-white/90">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--mk-mint))] text-[hsl(var(--mk-ink))]">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                {b}
              </Reveal>
            ))}
          </ul>
          <Reveal className="mt-10 flex flex-wrap gap-3">
            <CtaLink to="/demo">Have a nose around the demo</CtaLink>
            <CtaLink to="/features" variant="ghost" arrow={false}>
              See everything it does
            </CtaLink>
          </Reveal>
        </div>

        {/* Real schedule screenshot */}
        <Reveal delay={150} className="relative">
          <div className="rounded-[1.75rem] bg-white/10 p-2 shadow-[0_40px_80px_-30px_rgba(5,4,30,0.7)] ring-1 ring-white/15">
            <ProductShot
              src={scheduleTeamShot}
              alt="Today's schedule with every cleaner in their own column"
              width={1600}
              height={1000}
              className="border-0 shadow-none"
              testId="marketing-team-schedule"
            />
          </div>
          <div className="mk-float absolute -bottom-6 -left-4 hidden items-center gap-3 rounded-2xl bg-white px-4 py-3 text-[hsl(var(--mk-ink))] shadow-xl md:flex">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--mk-sun))]">
              <Star className="h-4 w-4" />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm font-semibold">Booked online</span>
              <span className="block text-xs text-[hsl(var(--mk-muted))]">while you were on a job</span>
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------ Pricing teaser ---------------------------- */

const plans = [
  // Prices come from the shared plan list (also used by the pricing page and structured data).
  { name: 'Solo', price: planByName('Solo').monthlyPrice, line: 'Just you and the mop bucket? Start here.' },
  { name: 'Team', price: planByName('Team').monthlyPrice, line: 'Up to 5 cleaners, your own domain and the AI logo maker.', featured: true },
  { name: 'Business', price: planByName('Business').monthlyPrice, line: 'However big the team gets, plus AI-written quotes and priority support.' },
];

const PricingTeaser = () => (
  <section className="py-28 md:py-36">
    <div className="container mx-auto px-4">
      <SectionHeading
        eyebrow="Simple pricing"
        title={
          <>
            Costs less than <span className="mk-mark mk-mark-mint">one clean a month.</span>
          </>
        }
        lede="Plans start at £9. No setup fee, no contract, and we never take a slice of what your customers pay you."
      />
      <div className="mx-auto mt-16 grid max-w-5xl gap-5 md:grid-cols-3">
        {plans.map((p, i) => (
          <Reveal
            key={p.name}
            delay={i * 90}
            className={cn(
              'mk-card mk-card-hover relative flex flex-col p-8',
              p.featured && 'border-[hsl(var(--mk-ink))] md:-translate-y-3'
            )}
          >
            {p.featured && (
              <span className="absolute -top-3.5 left-8 rounded-full bg-[hsl(var(--mk-sun))] px-3 py-1 text-xs font-semibold">
                Most popular
              </span>
            )}
            <span className="font-display text-xl font-semibold">{p.name}</span>
            <span className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-5xl font-semibold tracking-tight">£{p.price}</span>
              <span className="text-[hsl(var(--mk-muted))]">/month</span>
            </span>
            <span className="mt-3 flex-1 text-[hsl(var(--mk-muted))]">{p.line}</span>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 flex justify-center">
        <Link to="/pricing" className="mk-btn mk-btn-ink">
          Compare plans <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default MarketingHome;
